import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { Counter } from '@/components/site/Counter';
import { HeroImage } from '@/components/site/HeroImage';
import { SITE, SERVICES } from '@/data/site';

/**
 * Shared hero block for every service detail page.
 */
export function ServiceHero({
  serviceNum,           // '02', '03', ...
  serviceTitle,         // 'Web App Development'
  headlineText,         // 'Web applications that handle real work.'
  accent,               // single word inside headline to highlight
  description,          // paragraph
  primaryCta = { label: 'Book discovery call', href: SITE.whatsapp },
  secondaryCta,         // { label, to }
  testIdPrefix,         // 'wa', 'mob', 'cs', 'ec', 'ai'
  image,                // { src, alt }
}) {
  return (
    <section className="relative overflow-hidden">
      <MouseGlow intensity={0.3} />
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-20 md:pb-28">
        <nav
          data-testid={`${testIdPrefix}-breadcrumb`}
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink-muted"
        >
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight size={12} />
          <Link to="/services/" className="hover:text-white transition-colors">Services</Link>
          <ChevronRight size={12} />
          <span className="text-white">{serviceTitle}</span>
        </nav>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-x-10 md:gap-y-16">
          <div className="md:col-span-9">
            <SectionLabel as="h1" num={serviceNum} data-testid={`${testIdPrefix}-eyebrow`} className="mt-2">
              {SERVICES.find((s) => s.num === serviceNum)?.h1 || serviceTitle}
            </SectionLabel>
            <WordReveal
              as="p"
              text={headlineText}
              accent={accent}
              data-testid={`${testIdPrefix}-headline`}
              className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[78px] font-extrabold tracking-tightest leading-[1.02] text-balance"
            />
          </div>
          <div className="md:col-span-7 md:col-start-6 md:row-start-2">
            <Reveal delay={400}>
              <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl">
                {description}
              </p>
            </Reveal>
            <Reveal delay={520}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton
                  href={primaryCta.href}
                  external
                  variant="primary"
                  data-testid={`${testIdPrefix}-hero-cta-primary`}
                >
                  {primaryCta.label}
                  <ArrowUpRight size={16} />
                </MagneticButton>
                {secondaryCta && (
                  <MagneticButton
                    to={secondaryCta.to}
                    variant="ghost"
                    data-testid={`${testIdPrefix}-hero-cta-secondary`}
                  >
                    {secondaryCta.label}
                  </MagneticButton>
                )}
              </div>
            </Reveal>
          </div>
          {image && (
            <div
              className="md:col-span-5 md:col-start-1 md:row-start-2 self-end"
              data-testid={`${testIdPrefix}-hero-image`}
            >
              <HeroImage src={image.src} alt={image.alt} testId={`${testIdPrefix}-hero-image-frame`} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Text-link row to the location, cost and sub-service pages that belong to a
 * service. Descriptive anchors: this is the main internal-link path to them.
 */
export function PageLinks({ heading, intro, links, testIdPrefix }) {
  return (
    <section data-testid={`${testIdPrefix}-page-links`} className="relative py-20 md:py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight leading-[1.1]">{heading}</h2>
          {intro && <p className="mt-4 text-ink-muted leading-relaxed max-w-md">{intro}</p>}
        </div>
        <ul className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 self-start">
          {links.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="arrow-link inline-flex items-center gap-2 text-white/85 hover:text-white">
                {l.label} <span className="arrow text-ink-muted">→</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Three-card related services strip that excludes the current page's slug.
 */
export function RelatedServices({ exclude, testIdPrefix }) {
  const related = SERVICES.filter((s) => s.slug !== exclude).slice(0, 3);
  return (
    <section data-testid={`${testIdPrefix}-related`} className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <SectionLabel num="09">Related services</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              If this fits, these <span className="italic text-electric">might too.</span>
            </h2>
          </div>
          <Link to="/services/" className="arrow-link hidden md:inline-flex items-center gap-2 text-sm text-white">
            All services <span className="arrow">→</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {related.map((s, i) => (
            <Reveal key={s.slug} delay={i * 80}>
              <Link
                to={`/services/#${s.slug}`}
                data-testid={`${testIdPrefix}-related-${s.slug}`}
                className="group block h-full rounded-[16px] border border-white/[0.12] bg-ink-bg p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(108,92,231,0.45)]"
              >
                <div className="flex items-baseline justify-between">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">{s.num} / 06</div>
                  <ArrowUpRight size={16} className="text-ink-muted group-hover:text-white transition-colors" />
                </div>
                <div className="mt-6 text-2xl font-bold tracking-tight">{s.title}</div>
                <p className="mt-3 text-ink-muted leading-relaxed text-sm">{s.short}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Generic problems-we-solve block (4-card grid).
 * items: [{icon: LucideComponent, title, body, slug}]
 */
export function ProblemsBlock({ heading, accentWord, intro, items, testIdPrefix }) {
  return (
    <section data-testid={`${testIdPrefix}-problems`} className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="02">Problems we solve</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] text-balance">
            {heading.split(accentWord || '___')[0]}
            {accentWord && <span className="italic text-violet">{accentWord}</span>}
            {heading.split(accentWord || '___')[1]}
          </h2>
          {intro && <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">{intro}</p>}
        </div>
        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {items.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 80}>
                <div
                  data-testid={`${testIdPrefix}-problem-${p.slug}`}
                  className="group h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 md:p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(108,92,231,0.5)]"
                >
                  <div className="h-10 w-10 rounded-xl border border-white/[0.12] flex items-center justify-center text-violet bg-white/[0.02]">
                    <Icon size={18} />
                  </div>
                  <div className="mt-5 text-xl font-semibold tracking-tight">{p.title}</div>
                  <p className="mt-3 text-ink-muted leading-relaxed text-sm">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Generic "What we build" 6-card asymmetric grid.
 * items: [{tag, title, body, accent, slug}]
 */
export function BuildBlock({ heading, accentWord, intro, items, testIdPrefix }) {
  const spans = ['md:col-span-7', 'md:col-span-5', 'md:col-span-5', 'md:col-span-7', 'md:col-span-6', 'md:col-span-6'];
  return (
    <section data-testid={`${testIdPrefix}-what-we-build`} className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <SectionLabel num="03">What we build</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              {heading.split(accentWord || '___')[0]}
              {accentWord && <span className="italic text-electric">{accentWord}</span>}
              {heading.split(accentWord || '___')[1]}
            </h2>
          </div>
          {intro && (
            <div className="md:col-span-6 md:col-start-7">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-lg">{intro}</p>
              </Reveal>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {items.map((w, i) => (
            <Reveal key={w.title} delay={i * 70} className={spans[i % spans.length]}>
              <div
                data-testid={`${testIdPrefix}-build-${w.slug}`}
                className="group relative h-full overflow-hidden rounded-[16px] border border-white/[0.12] bg-ink-bg p-6 md:p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(75,159,255,0.45)]"
              >
                <div
                  className="absolute -right-16 -top-16 h-44 w-44 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-700"
                  style={{ background: w.accent }}
                />
                <div className="relative flex h-full flex-col">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-ink-muted">{w.tag}</div>
                  <div className="mt-4 flex items-baseline justify-between">
                    <div className="text-2xl md:text-3xl font-bold tracking-tight">{w.title}</div>
                    <ArrowUpRight size={18} className="arrow-link arrow text-ink-muted group-hover:text-white transition-colors" />
                  </div>
                  <p className="mt-4 text-ink-muted leading-relaxed text-sm max-w-md">{w.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Process / approach block — vertical stack of step cards.
 */
export function ApproachBlock({ heading, accentWord, intro, steps, testIdPrefix, num = '04' }) {
  return (
    <section data-testid={`${testIdPrefix}-approach`} className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num={num}>Our approach</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            {heading.split(accentWord || '___')[0]}
            {accentWord && <span className="italic text-violet">{accentWord}</span>}
            {heading.split(accentWord || '___')[1]}
          </h2>
          {intro && <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">{intro}</p>}
        </div>
        <div className="md:col-span-8 space-y-4">
          {steps.map((p, i) => (
            <Reveal key={p.step} delay={i * 70}>
              <div
                data-testid={`${testIdPrefix}-process-${p.slug}`}
                className="group rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 md:p-7 transition-all duration-500 hover:border-white/25 hover:-translate-y-0.5"
              >
                <div className="flex items-baseline gap-6">
                  <div className="text-violet tabular-nums text-sm">{p.step}</div>
                  <div className="h-px w-12 bg-white/15" />
                  <div className="text-xl md:text-2xl font-semibold tracking-tight">{p.title}</div>
                </div>
                <p className="mt-4 ml-[68px] text-ink-muted leading-relaxed max-w-2xl">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Counters grid (4 cells). values: [{kpi, prefix, suffix, label, decimals}]
 */
export function OutcomesBlock({ heading, accentWord, intro, values, testIdPrefix, num = '07' }) {
  // Lazy import to avoid circular
  const { Counter } = require('@/components/site/Counter');
  return (
    <section data-testid={`${testIdPrefix}-outcomes`} className="relative py-24 md:py-32 bg-ink-surface/30 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <SectionLabel num={num}>Outcomes</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              {heading.split(accentWord || '___')[0]}
              {accentWord && <span className="italic text-electric">{accentWord}</span>}
              {heading.split(accentWord || '___')[1]}
            </h2>
          </div>
          {intro && (
            <div className="md:col-span-5 md:col-start-8">
              <Reveal>
                <p className="text-ink-muted leading-relaxed">{intro}</p>
              </Reveal>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
          {values.map((o, i) => (
            <Reveal key={o.label} delay={i * 90}>
              <div data-testid={`${testIdPrefix}-outcome-${i}`} className="border-l border-white/[0.12] pl-5">
                <div className="text-5xl md:text-6xl font-extrabold tracking-tight leading-none">
                  <Counter
                    value={o.kpi}
                    prefix={o.prefix || ''}
                    suffix={o.suffix || ''}
                    decimals={o.decimals || (!Number.isInteger(o.kpi) ? 1 : 0)}
                  />
                </div>
                <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                  {o.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
